/**
 * The four seller areas (P25009-386), promoted by P25009-431 to THE single
 * source for the whole account rail.
 *
 * "Group the seller tools into four clear areas: Sell, Orders & money, Run the
 * workshop, and Account." The grouping follows the merchant's own sequence —
 * sell, get paid, run the workshop, administer — rather than the order the pages
 * happened to be built in.
 *
 * P25009-431 BR-1/AC-1: the avatar dropdown is identity-only, so every account
 * item that used to live there (Inköp, Statistik, Dina favoriter, Meddelanden
 * via the inbox rail) must have exactly one home here. Placement follows the
 * client mockup's "Where every existing item lands" table ("Wheelplace
 * Navsystem" artifact), which also retires the old three-per-area cap (386
 * BR-2) — the test keeps a loose guard of seven, the size Order & pengar
 * reaches in the mockup.
 *
 * Dina bilar stays private-only; the mockup keeps it as a saved-things page
 * behind the car icon, not a rail row.
 */

export const GROUP = {
  SELL: 'sell',
  MONEY: 'money',
  WORKSHOP: 'workshop',
  ACCOUNT: 'account',
};

/**
 * How far an item works on a phone (P25009-443 BR-2/BR-4).
 *
 * The mobile drawer shows the SAME groups, order and names as this rail —
 * that is the whole point of BR-1 — but splits the items into three levels:
 *   FULL     works on mobile like anywhere else (the default; the line runs
 *            between "what cannot wait" and desk work, not buyer vs seller)
 *   VIEW     shown, opens, but the phone surface is read-only ("have I been
 *            paid" is a number you check on a phone; the exports are not)
 *   DESKTOP  file handling, wide tables, contracts — collected at the end of
 *            the drawer under "Finns på datorn", dimmed, with the
 *            open-on-a-computer note. Desktop rendering ignores this field.
 */
export const MOBILE_LEVEL = {
  FULL: 'full',
  VIEW: 'view',
  DESKTOP: 'desktop',
};

/**
 * `badge` names which count from the nav's props lights up the item, so the
 * component does not need a switch over item ids.
 *
 * `businessOnly` marks items the Sharetribe config only exposes to business
 * accounts; `module` marks ones gated on admin-set metadata.
 */
export const SELLER_NAV_GROUPS = [
  {
    id: GROUP.SELL,
    labelKey: 'SellerNav.group.sell',
    items: [
      { id: 'listings', labelKey: 'SellerNav.listings', route: 'ManageListingsPage', badge: 'drafts' },
      { id: 'packages', labelKey: 'SellerNav.packages', route: 'WheelplaceBuildPackagesPage', mobileLevel: MOBILE_LEVEL.DESKTOP },
      /* Hjulprogram (P25009-405) is deliberately NOT an item here even after the
       * cap moved to five: it is reached from the Bygg paket page instead, which
       * is where a dealer is already thinking about packages. */
      { id: 'bulk', labelKey: 'SellerNav.bulkUpload', route: 'WheelplaceBulkUploadPage', mobileLevel: MOBILE_LEVEL.DESKTOP },
    ],
  },
  {
    id: GROUP.MONEY,
    labelKey: 'SellerNav.group.money',
    items: [
      /* P25009-390 BR-5 — "Försäljning" opens the ORDER list.
       *
       * It pointed at `InboxPage` with `tab: 'sales'`, the message inbox, while
       * the mobile drawer's item with the same label "Försäljning" pointed at
       * `SalePage`. One label, two destinations, which is precisely the
       * navigation inconsistency BR-5 names. Orders are acted on at /sale. */
      { id: 'sales', labelKey: 'SellerNav.sales', route: 'SalePage', badge: 'newOrders' },
      /* Inköp lived only in the avatar dropdown (P25009-431 empties it):
       * dealers buy from each other, and a purchase is an order too. */
      /* The Frakt queue (P25009-450 BR-6): shipping orders waiting for a
       * booking, with a count — work with a deadline attached earns a rail row
       * rather than a tab inside Försäljning, where nobody sees it until it is
       * late. This is the seventh Order & pengar item the cap comment
       * anticipated. Pickup orders never enter the queue. */
      { id: 'freight', labelKey: 'SellerNav.freight', route: 'FreightPage', badge: 'freightQueue' },
      { id: 'purchases', labelKey: 'SellerNav.purchases', route: 'OrderPage' },
      /* The one nav item allowed to point at the inbox — it IS the messages
       * item (see the destination guard in the test). tab is a required path
       * segment of /inbox/:tab; 'sales' is the seller side of the conversation
       * list, mirroring what the inbox's own rail opens for a seller. */
      { id: 'messages', labelKey: 'SellerNav.messages', route: 'InboxPage', routeParams: { tab: 'sales' } },
      { id: 'finance', labelKey: 'SellerNav.finance', route: 'StripeDashboardPage', mobileLevel: MOBILE_LEVEL.VIEW },
      { id: 'subscription', labelKey: 'SellerNav.subscription', route: 'SubscriptionsPage', businessOnly: true, mobileLevel: MOBILE_LEVEL.DESKTOP },
      /* Statistik lived only in the avatar dropdown. The client mockup files it
       * under Order & pengar, last — it reads money moving, not selling. */
      { id: 'statistics', labelKey: 'SellerNav.statistics', route: 'LemonadoStatisticsPage', mobileLevel: MOBILE_LEVEL.VIEW },
    ],
  },
  {
    id: GROUP.WORKSHOP,
    labelKey: 'SellerNav.group.workshop',
    items: [
      { id: 'services', labelKey: 'SellerNav.services', route: 'ManageServicesPage', sellerOrBusiness: true, mobileLevel: MOBILE_LEVEL.DESKTOP },
      { id: 'storage', labelKey: 'SellerNav.storage', route: 'ManageStoragePage', module: 'storage' },
      { id: 'addresses', labelKey: 'SellerNav.addresses', route: 'ManageWarehousePage' },
    ],
  },
  {
    id: GROUP.ACCOUNT,
    labelKey: 'SellerNav.group.account',
    items: [
      { id: 'shopProfile', labelKey: 'SellerNav.shopProfile', route: 'ProfileSettingsPage', mobileLevel: MOBILE_LEVEL.VIEW },
      { id: 'payouts', labelKey: 'SellerNav.payouts', route: 'StripePayoutPage', badge: 'payoutBlocked', mobileLevel: MOBILE_LEVEL.DESKTOP },
      { id: 'settings', labelKey: 'SellerNav.settings', route: 'ContactDetailsPage', mobileLevel: MOBILE_LEVEL.DESKTOP },
      /* Integrationer (P25009-412) — company settings, Enterprise only (BR-5).
       * Desktop: it is configuration for an IT team, not a phone task. */
      { id: 'integrations', labelKey: 'SellerNav.integrations', route: 'IntegrationsPage', enterpriseOnly: true, mobileLevel: MOBILE_LEVEL.DESKTOP },
      /* 386 BR-4 kept Favoriter off the seller rail; the P25009-431 mockup's
       * move list explicitly reverses that — "Dina favoriter → Rail, Konto —
       * plus the heart icon in the bar". Dina bilar stays private-only. */
      { id: 'favourites', labelKey: 'SellerNav.favourites', route: 'ManageFavouritesPage' },
    ],
  },
];

/**
 * Which items this particular seller should see.
 *
 * The gates mirror what the old flat UserNav did, so nobody loses access to a
 * page they had yesterday:
 *   - `sellerOrBusiness` — userType seller or business
 *   - `businessOnly`     — userType business
 *   - `module`           — admin-set metadata, either `<name>Enabled` or an
 *                          entry in `metadata.modules` (the commissionTier
 *                          pattern used by the storage module)
 *   - `enterpriseOnly`   — metadata.subscriptionName 'enterprise', the same
 *                          check bulk upload and /api/integrations make
 */
export const visibleGroupsFor = currentUser => {
  const profile = currentUser?.attributes?.profile;
  const userType = profile?.publicData?.userType;
  const metadata = profile?.metadata || {};

  const hasModule = name =>
    metadata[`${name}Enabled`] === true ||
    (Array.isArray(metadata.modules) && metadata.modules.includes(name));

  const allowed = item => {
    if (item.businessOnly && userType !== 'business') return false;
    if (item.sellerOrBusiness && userType !== 'seller' && userType !== 'business') return false;
    if (item.module && !hasModule(item.module)) return false;
    if (item.enterpriseOnly && metadata.subscriptionName !== 'enterprise') return false;
    return true;
  };

  return SELLER_NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(allowed),
  })).filter(group => group.items.length > 0);
};

/**
 * The drawer's view of the rail (P25009-443 BR-1/BR-2): the same groups in
 * the same order, minus the desktop-only items — those are returned
 * separately so the drawer can collect them at the end under "Finns på
 * datorn", dimmed. Items keep their `mobileLevel` so the drawer can mark the
 * view-only ones.
 */
export const mobileGroupsFor = currentUser =>
  visibleGroupsFor(currentUser)
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.mobileLevel !== MOBILE_LEVEL.DESKTOP),
    }))
    .filter(group => group.items.length > 0);

/** The dimmed tail of the drawer: everything this seller has that needs a computer. */
export const mobileDesktopOnlyFor = currentUser =>
  visibleGroupsFor(currentUser)
    .flatMap(group => group.items)
    .filter(item => item.mobileLevel === MOBILE_LEVEL.DESKTOP);

/**
 * Which group contains the page currently open, so its area can be marked
 * active on mobile where only one group is expanded at a time.
 */
export const groupIdForPage = currentPage => {
  const match = SELLER_NAV_GROUPS.find(g => g.items.some(i => i.route === currentPage));
  return match ? match.id : null;
};

/** Sum of the badge counts inside a group — BR-2's "badge shows where something needs action". */
export const groupBadgeCount = (group, badges = {}) =>
  (group?.items || []).reduce((total, item) => {
    if (!item.badge) return total;
    const value = badges[item.badge];
    /* Booleans (payouts blocked) contribute presence, not a number: "1" next to
     * Utbetalningar would read as one pending payout. */
    if (value === true) return total + 1;
    return total + (typeof value === 'number' ? value : 0);
  }, 0);
